/**
 * HTTP service for the versions (work packages) feature (PX-W6.3,
 * TASK-EDD04F2F). Mutations return the affected row; the slice refetches the
 * list on fulfilment (no optimistic version writes — status transitions are
 * server-validated chains).
 */

import { apiClient } from '@/services'
import type { CreateVersionInput, VersionRow } from '../types'

const base = (orgId: string, projectId: string): string => `/${orgId}/projects/${projectId}/versions`

export const versionService = {
  /** Every work package of the project (`GET …/versions`). */
  async list(orgId: string, projectId: string): Promise<VersionRow[]> {
    return apiClient.get<VersionRow[]>(base(orgId, projectId))
  },

  /** One version (`GET …/versions/:versionId`). */
  async get(orgId: string, projectId: string, versionId: string): Promise<VersionRow> {
    return apiClient.get<VersionRow>(`${base(orgId, projectId)}/${versionId}`)
  },

  /** Create a work package (`POST …/versions`, 201 → the created row). */
  async create(orgId: string, projectId: string, input: CreateVersionInput): Promise<VersionRow> {
    return apiClient.post<VersionRow>(base(orgId, projectId), input)
  },

  /** Activate (`POST …/:versionId/activate` → the activated row). */
  async activate(orgId: string, projectId: string, versionId: string): Promise<VersionRow> {
    return apiClient.post<VersionRow>(`${base(orgId, projectId)}/${versionId}/activate`)
  },

  /** Archive (`POST …/:versionId/archive` → the archived row). */
  async archive(orgId: string, projectId: string, versionId: string): Promise<VersionRow> {
    return apiClient.post<VersionRow>(`${base(orgId, projectId)}/${versionId}/archive`)
  },

  /** Delete (`DELETE …/:versionId?archive=` → 200/204, no body). */
  async remove(orgId: string, projectId: string, versionId: string, archive = false): Promise<void> {
    return apiClient.delete<void>(
      `${base(orgId, projectId)}/${versionId}?archive=${archive ? 'true' : 'false'}`
    )
  }
}
